import { Button, ButtonLevel, Separator, Text, TextColor, TextSize } from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { Select, SelectContent, SelectItem, SelectPopover, SelectTrigger, SelectValue } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { Fragment, type Key, useRef } from 'react';

interface ClearableSelectItem {
  /**
   * Unique identifier for the item.
   */
  id: string;
  /**
   * Display label for the item.
   */
  label: string;
  /**
   * Optional color indicator class (e.g. 'bg-success-400').
   * When provided, renders a colored dot to the left of the label.
   */
  color?: string;
  /**
   * Optional range text displayed in bold before the label (e.g. '80-100').
   */
  range?: string;
  /**
   * Optional icon element rendered before the label.
   */
  icon?: React.ReactNode;
  /**
   * Whether a divider line precedes the item in the option list.
   */
  hasSeparatorBefore?: boolean;
}

interface ClearableSelectProps {
  /**
   * Accessible label for the select.
   */
  ariaLabel: string;
  /**
   * Placeholder text shown when no item is selected.
   * Also used as the label for the "All" option.
   */
  placeholder: string;
  /**
   * List of selectable items. An "All" option is automatically prepended.
   */
  items: ClearableSelectItem[];
  /**
   * The currently selected item key, or null when "All" is selected.
   */
  selectedKey: string | null;
  /**
   * Callback fired when the selection changes.
   * Receives the selected key, or null when "All" is chosen.
   */
  onSelectionChange: (key: string | null) => void;
  /**
   * Optional additional class name for the container.
   */
  className?: string;
  /**
   * Whether to prepend an "All" option that clears the selection
   *
   * @optional
   * @default true
   */
  includeAllOption?: boolean;
  /**
   * Whether to show the clear button when an item is selected
   *
   * @optional
   * @default true
   */
  isClearable?: boolean;
  /**
   * Optional additional class name for the select popover content.
   */
  contentClassName?: string;
}

const ALL_KEY = '__all__';

function ClearableSelectItemContent({ item }: { item: ClearableSelectItem }) {
  const hasIndicator = item.icon || item.color || item.range;

  if (!hasIndicator) {
    return (
      <Text elementType="p" size={TextSize.Md}>
        {item.label}
      </Text>
    );
  }

  return (
    <div className="flex items-center gap-6">
      {item.icon}
      {item.color && (
        <div className="flex h-5 w-5 items-center justify-center">
          <div className={cx('h-4 w-4 rounded-full', item.color)} />
        </div>
      )}
      {item.range && (
        <Text elementType="span" size={TextSize.Md}>
          {item.range}
        </Text>
      )}
      <Text elementType="span" size={TextSize.Md} color={item.range ? TextColor.Tertiary : TextColor.Primary}>
        {item.label}
      </Text>
    </div>
  );
}

function ClearableSelect(props: ClearableSelectProps) {
  const {
    ariaLabel,
    placeholder,
    items,
    selectedKey,
    onSelectionChange,
    includeAllOption = true,
    isClearable = true,
    className,
    contentClassName,
    ...other
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedItem = items.find((item) => item.id === selectedKey);

  const handleSelectionChange = (key: Key | null) => {
    if (includeAllOption && (key === ALL_KEY || key === null)) {
      onSelectionChange(null);
      return;
    }

    if (key) {
      onSelectionChange(key as string);
    }
  };

  const handleClear = () => {
    onSelectionChange(null);
    requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLButtonElement>('[data-rac]')?.focus();
    });
  };

  return (
    <div ref={containerRef} className={cx('relative', className)} {...other}>
      <Select selectedKey={selectedKey} onSelectionChange={handleSelectionChange} aria-label={ariaLabel}>
        <div className="flex flex-col gap-3">
          <SelectTrigger>
            {selectedItem ? (
              <ClearableSelectItemContent item={selectedItem} />
            ) : (
              <SelectValue className="data-[placeholder]:text-primary" placeholder={placeholder} />
            )}
          </SelectTrigger>
        </div>
        <SelectPopover>
          <SelectContent className={contentClassName}>
            {includeAllOption && (
              <SelectItem key={ALL_KEY} id={ALL_KEY} textValue="All">
                <Text elementType="p" size={TextSize.Md}>
                  All
                </Text>
              </SelectItem>
            )}
            {items.map((item) => {
              const textValue = item.range ? `${item.range} ${item.label}` : item.label;
              return (
                <Fragment key={item.id}>
                  {item.hasSeparatorBefore && <Separator className="my-2" />}
                  <SelectItem id={item.id} textValue={textValue}>
                    <ClearableSelectItemContent item={item} />
                  </SelectItem>
                </Fragment>
              );
            })}
          </SelectContent>
        </SelectPopover>
      </Select>
      {isClearable && selectedKey && (
        <Button
          aria-label="Clear selection"
          level={ButtonLevel['Text-Secondary']}
          className="text-gray-utility-400 hover:text-gray-utility-600 absolute right-14 top-1/2 -translate-y-1/2 z-10"
          onPress={handleClear}
        >
          <XCloseIcon size={16} />
        </Button>
      )}
    </div>
  );
}

export { ClearableSelect };
export type { ClearableSelectProps, ClearableSelectItem };
